/**
 * ThermasolEngine/Debug/DebugTypes.ts — Vague 17.1 (Fév 2026)
 * ----------------------------------------------------------------
 * Types du système Debug Global THERMASOL AirSystems®.
 *
 * PRINCIPE :
 *   Chaque moteur émet des « traces » (formules, règles, warnings)
 *   dans un registre central. Le mode Debug ne modifie JAMAIS les
 *   calculs — il collecte uniquement, avec un coût quasi-nul quand
 *   `enabled = false` (les appels sont wrappés en callbacks lazy).
 *
 * COUVERTURE (les 13 points du cahier des charges Léon) :
 *   1. Données d'entrée      → TraceInput[]
 *   2. Variables intermédiaires → TraceIntermediate[]
 *   3. Formules              → FormulaTrace
 *   4. Détail ligne-par-ligne → FormulaTrace.lines
 *   5. Traçabilité           → SourceRef
 *   6. Normes                → NormRef[]
 *   7. Historique            → DebugSession[]
 *   8. Incohérences          → SelfCheckResult (mismatch expected vs actual)
 *   9. Self-check auto       → SelfCheckResult
 *  10. Export                → JSON/CSV/PDF (DebugRegistry.export*)
 *  11. Dev mode              → DebugPerfMetrics
 *  12. Navigation            → BuildingScope + ChamberScope
 *  13. Assistant diagnostic  → DiagnosticProposal[] (Vague 17.1)
 */

// ─────────────────────────────────────────────────────────────
// Enums & primitives
// ─────────────────────────────────────────────────────────────

export type Severity = "PASS" | "WARN" | "FAIL" | "INFO" | "NOT_APPLICABLE";

/** Origine d'une donnée d'entrée. */
export type InputOrigin =
  | "user"          // saisie utilisateur
  | "computed"      // calcul interne (par un autre moteur)
  | "catalog"       // catalogue matériel (Aster, IPN, panneaux...)
  | "pvgis"         // API PVGIS
  | "openmeteo"     // API Open-Meteo
  | "api"           // autre API externe
  | "imposed"       // valeur imposée par une norme
  | "fallback"      // valeur par défaut (fallback)
  | "erp"           // ERP interne
  | "config"        // configuration admin (MongoDB)
  | "constant";     // constante physique (g, ρ, cp, ...)

/** Nom canonique des moteurs. Étendre au fil des vagues. */
export type EngineName =
  | "CEEEngine"
  | "PVEngine"
  | "ThermalEngine"
  | "ElectricalEngine"
  | "HVACSizingEngine"
  | "HVACDryingEngine"
  | "DryingEngine"
  | "ClimateEngine"
  | "StructureEngine"
  | "EurocodeEngine"
  | "EngineeringContext"
  | "BuildingOptimizer"
  | "AutoOptimizer"
  | "EconomicEngine"
  | "QuoteBuilder"
  | "PDFBuilder"
  | "FirewoodEngine"
  | "RegulationEngine"
  | "ERPEngine"
  | "RoofEngine"
  | "AeraulicEngine";

// ─────────────────────────────────────────────────────────────
// Traces d'entrée / intermédiaires / sortie
// ─────────────────────────────────────────────────────────────

export interface TraceInput {
  readonly key: string;             // "surface_toiture_m2"
  readonly label: string;           // "Surface toiture"
  readonly value: number | string | boolean | null;
  readonly unit?: string;           // "m²", "kW", "°C", ...
  readonly origin: InputOrigin;
  readonly source_detail?: string;  // "PVGIS 2020, Nancy 48.68°N"
}

export interface TraceIntermediate {
  readonly key: string;             // "cumac_kwh"
  readonly label: string;           // "CEE cumac"
  readonly value: number | string | null;
  readonly unit?: string;
  readonly computed_from?: string[];  // ["kwth_total", "coef_agri_eq_110_h2"]
}

// ─────────────────────────────────────────────────────────────
// Formule (le cœur du Debug)
// ─────────────────────────────────────────────────────────────

/**
 * Une ligne détaillée d'un calcul (§4 : ligne-par-ligne).
 * Ex. pour un devis :   {label: "Panneaux", value: 42500, unit: "€"}
 * Ex. pour ELU        : {label: "1.35 × G", value: 4.05, unit: "kN/m²"}
 */
export interface FormulaLine {
  readonly label: string;
  readonly expr?: string;           // "1.35 × 3.00"
  readonly value: number;
  readonly unit?: string;
}

export interface FormulaTrace {
  readonly id: string;              // "cee.prime_cee_eur"
  readonly engine: EngineName;
  readonly engine_version: string;
  readonly label: string;           // "Prime CEE versée client (Vague 16, 50%)"
  readonly formula: string;         // "prime_client = cumac × valeur_kwh × 0.50"
  readonly formula_latex?: string;  // "P_{client} = C \\times v \\times 0.50"
  readonly inputs: Record<string, number | string | boolean | null>;
  readonly intermediates?: Record<string, number | string | null>;
  readonly lines?: FormulaLine[];   // §4 : détail ligne par ligne
  readonly result: number | string;
  readonly result_unit?: string;
  readonly severity: Severity;      // PASS par défaut
  readonly self_check?: SelfCheckResult;  // §8+9 : recompute + compare
  readonly diagnostics?: DiagnosticProposal[];  // §13 : assistant
  readonly norms?: NormRef[];       // §6
  readonly source: SourceRef;       // §5 : traçabilité
  readonly scope?: TraceScope;      // §12 : navigation
  readonly timestamp: string;       // ISO
  readonly duration_ms?: number;    // §11 : dev mode
}

// ─────────────────────────────────────────────────────────────
// Règles (Eurocode, CEE, HVAC, ...)
// ─────────────────────────────────────────────────────────────

export interface RuleTrace {
  readonly rule_id: string;         // "EC1-4-vent-cat-II"
  readonly engine: EngineName;
  readonly engine_version: string;
  readonly label: string;           // "Vérification vent (Eurocode 1-4)"
  readonly expected: string;        // "q_ref ≤ 900 Pa"
  readonly observed: string;        // "820 Pa"
  readonly status: Severity;        // PASS/WARN/FAIL/NOT_APPLICABLE
  readonly norm: NormRef;
  readonly message?: string;
  readonly diagnostics?: DiagnosticProposal[];  // §13
  readonly formula_ref?: string;    // id d'une FormulaTrace liée
  readonly scope?: TraceScope;
  readonly timestamp: string;
}

// ─────────────────────────────────────────────────────────────
// Warnings & Erreurs
// ─────────────────────────────────────────────────────────────

export interface WarningTrace {
  readonly code: string;            // "STRUCT_CAPA_INSUFFISANTE"
  readonly engine: EngineName;
  readonly engine_version: string;
  readonly title: string;
  readonly message: string;
  readonly severity: "INFO" | "WARN" | "FAIL";
  readonly diagnostics: DiagnosticProposal[];  // §13 : OBLIGATOIRE si WARN/FAIL
  readonly norms?: NormRef[];
  readonly scope?: TraceScope;
  readonly timestamp: string;
}

// ─────────────────────────────────────────────────────────────
// Self-Check automatique (§8 + §9)
// ─────────────────────────────────────────────────────────────

export interface SelfCheckResult {
  readonly ok: boolean;
  readonly expected_value: number;
  readonly actual_value: number;
  readonly diff_abs: number;
  readonly diff_pct: number;         // 0 = parfait
  readonly tolerance_pct: number;    // typiquement 0.01 (=1%)
  readonly recomputed_from: string;  // "cumac_kwh × VALEUR_KWH_CUMAC × 0.50"
  readonly reason?: string;          // détails si NOK
}

// ─────────────────────────────────────────────────────────────
// Assistant Diagnostic (§13) — CŒUR DE LA DEMANDE LÉON
// ─────────────────────────────────────────────────────────────

/**
 * Type d'action recommandée pour corriger un WARN/FAIL.
 * Ces types sont matérialisés dans /Debug/DiagnosticLibrary.ts
 * et rendus dans DiagnosticsTab avec CTA "Appliquer" quand possible.
 */
export type DiagnosticActionType =
  | "avis_be_requis"
  | "add_building"
  | "reinforce_structure"
  | "change_roof_slope"
  | "increase_cta_flow"
  | "change_duct_diameter"
  | "increase_thermal_power"
  | "change_drying_mode"       // plancher ↔ HVAC
  | "change_product_category"
  | "change_zone_climatique"
  | "change_orientation"
  | "add_inverter"
  | "increase_valeur_kwh_cumac"
  | "verify_input"
  | "consult_expert"
  | "info_only";

export interface DiagnosticProposal {
  readonly action_type: DiagnosticActionType;
  readonly title: string;           // "Réduire le nombre de panneaux de 12 %"
  readonly reason: string;          // "La charpente actuelle ne supporte que 3.20 kN/m² ELU"
  readonly variable_cause?: string; // "nb_panneaux" ou "capacite_pondere_kn_m2"
  readonly rule_violated?: string;  // "EC0-6.4.3 : Ed_ELU ≤ Rd_ELU"
  readonly norm?: string;           // "NF EN 1990"
  readonly delta_suggested?: number;
  readonly delta_unit?: string;
  readonly expected_outcome?: string;  // "coefficient sécurité passe de 0.98 à 1.15"
  readonly severity: Severity;
  readonly auto_applicable?: boolean;  // true = bouton "Appliquer" possible
  readonly priority?: 1 | 2 | 3;       // 1 = urgente, 3 = suggestion
}

// ─────────────────────────────────────────────────────────────
// Traçabilité (§5) & Scope (§12)
// ─────────────────────────────────────────────────────────────

export interface SourceRef {
  readonly engine: EngineName;
  readonly engine_version: string;
  readonly file?: string;           // "ThermasolEngine/CEEEngine/index.ts"
  readonly function?: string;       // "computeCEE"
  readonly commit?: string;         // git commit court (optionnel)
  readonly date: string;            // ISO
}

export interface NormRef {
  readonly code: string;            // "NF EN 1991-1-4"
  readonly label: string;           // "Eurocode 1-4 (vent)"
  readonly clause?: string;         // "§4.5.1"
  readonly year?: number;
}

export interface TraceScope {
  readonly project_ref?: string;    // "PROJ-2026-042"
  readonly building_id?: string;    // "bat-A"
  readonly building_label?: string; // "Bâtiment A - Séchoir 1"
  readonly chamber_id?: string;
  readonly chamber_label?: string;
}

// ─────────────────────────────────────────────────────────────
// Session Debug (agrégat d'un run de calcul)
// ─────────────────────────────────────────────────────────────

export interface DebugPerfMetrics {
  readonly total_duration_ms: number;
  readonly total_traces: number;
  readonly total_warnings: number;
  readonly total_errors: number;
  readonly per_engine_duration_ms: Record<string, number>;
  readonly per_engine_traces: Record<string, number>;
}

export interface DebugSession {
  readonly session_id: string;      // uuid
  readonly project_ref?: string;
  readonly started_at: string;      // ISO
  readonly ended_at?: string;
  readonly formulas: FormulaTrace[];
  readonly rules: RuleTrace[];
  readonly warnings: WarningTrace[];
  readonly perf: DebugPerfMetrics;
  readonly self_check_summary: {
    readonly total: number;
    readonly pass: number;
    readonly warn: number;
    readonly fail: number;
  };
}

// ─────────────────────────────────────────────────────────────
// Utilitaires
// ─────────────────────────────────────────────────────────────

export function nowIso(): string {
  return new Date().toISOString();
}

/** Génère un id trace stable-ish (pas de dépendance crypto). */
export function makeTraceId(prefix = "t"): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`;
}
